<template>
  <div class="templates-page">
    <CommonOverlay :show="loading" title="Loading Templates" message="Please wait..." />

    <!-- Page Header -->
    <div class="templates-header">
      <div class="header-left">
        <h1 class="page-title">Email Templates</h1>
        <p class="page-subtitle">Manage your campaign email templates</p>
      </div>
      <div class="header-actions">
        <b-button variant="success" @click="openCreateTemplateModal">
          <i class="fa fa-plus mr-2"></i> Create Template
        </b-button>
      </div>
    </div>

    <!-- Sub-navigation for Campaigns section -->
    <div class="campaigns-subnav">
      <router-link to="/app/campaigns" class="subnav-link">
        <i class="fa fa-bullhorn"></i>
        <span>Campaigns</span>
      </router-link>
      <router-link to="/app/campaigns-drafts" class="subnav-link">
        <i class="fa fa-pencil-square-o"></i>
        <span>Drafts</span>
      </router-link>
      <router-link to="/app/campaigns-statistics" class="subnav-link">
        <i class="fa fa-bar-chart"></i>
        <span>Reports</span>
      </router-link>
      <router-link
        to="/app/campaigns-templates"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-templates' }"
      >
        <i class="fa fa-file-text-o"></i>
        <span>Templates</span>
      </router-link>
      <router-link to="/app/campaigns-ab-tests" class="subnav-link">
        <i class="fa fa-flask"></i>
        <span>A/B Tests</span>
      </router-link>
    </div>

    <!-- Search and Filter -->
    <div class="templates-toolbar">
      <div class="search-box">
        <i class="fa fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search templates..."
          class="search-input"
        />
        <i
          v-if="searchQuery"
          class="fa fa-times-circle clear-btn"
          @click="searchQuery = ''"
        ></i>
      </div>

      <div class="filter-group">
        <b-form-select v-model="filterType" :options="typeOptions" class="filter-select"></b-form-select>
      </div>

      <div class="view-toggle">
        <button
          class="view-btn"
          :class="{ active: viewMode === 'grid' }"
          @click="viewMode = 'grid'"
        >
          <i class="fa fa-th-large"></i>
        </button>
        <button
          class="view-btn"
          :class="{ active: viewMode === 'list' }"
          @click="viewMode = 'list'"
        >
          <i class="fa fa-list"></i>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && filteredTemplates.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fa fa-file-text-o"></i>
      </div>
      <h4>No Templates Found</h4>
      <p v-if="searchQuery">Try adjusting your search query</p>
      <p v-else>Get started by creating your first email template</p>
      <b-button v-if="!searchQuery" variant="primary" @click="openCreateTemplateModal">
        <i class="fa fa-plus mr-2"></i> Create Template
      </b-button>
    </div>

    <!-- Grid View -->
    <div v-if="viewMode === 'grid' && filteredTemplates.length > 0" class="templates-grid">
      <div
        v-for="template in paginatedTemplates"
        :key="template.TemplateID"
        class="template-card"
        @click="editTemplate(template)"
      >
        <div class="card-preview">
          <div class="preview-placeholder">
            <i :class="getTypeIcon(template.Type)"></i>
          </div>
        </div>
        <div class="card-content">
          <h5 class="template-name">{{ template.Name }}</h5>
          <div class="template-meta">
            <span class="type-badge" :class="'type-' + template.Type.toLowerCase()">
              {{ template.Type }}
            </span>
            <span class="usage-count">
              <i class="fa fa-paper-plane"></i> {{ template.UsageCount }} uses
            </span>
          </div>
          <div class="template-date">
            Created {{ template.CreatedAt | relativeDate }}
          </div>
        </div>
        <div class="card-actions" @click.stop>
          <b-dropdown variant="link" no-caret right toggle-class="action-btn">
            <template #button-content>
              <i class="fa fa-ellipsis-v"></i>
            </template>
            <b-dropdown-item @click="createNewsletter(template)">
              <i class="fa fa-paper-plane mr-2"></i> Create Newsletter
            </b-dropdown-item>
            <b-dropdown-item @click="renameTemplate(template)">
              <i class="fa fa-i-cursor mr-2"></i> Rename
            </b-dropdown-item>
            <b-dropdown-item @click="editTemplate(template)">
              <i class="fa fa-pencil mr-2"></i> Edit
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="duplicateTemplate(template)">
              <i class="fa fa-copy mr-2"></i> Duplicate
            </b-dropdown-item>
            <b-dropdown-item @click="downloadHtml(template)">
              <i class="fa fa-download mr-2"></i> Download HTML
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="confirmDeleteTemplate(template)" class="text-danger">
              <i class="fa fa-trash mr-2"></i> Delete
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-if="viewMode === 'list' && filteredTemplates.length > 0" class="templates-list">
      <table class="templates-table">
        <thead>
          <tr>
            <th class="sortable" @click="sortBy('Name')">
              Name
              <i :class="getSortIcon('Name')"></i>
            </th>
            <th>Type</th>
            <th class="sortable" @click="sortBy('CreatedAt')">
              Created on
              <i :class="getSortIcon('CreatedAt')"></i>
            </th>
            <th class="sortable" @click="sortBy('UsageCount')">
              Uses
              <i :class="getSortIcon('UsageCount')"></i>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="template in paginatedTemplates" :key="template.TemplateID">
            <td class="name-cell" @click="editTemplate(template)">
              <i :class="getTypeIcon(template.Type)" class="mr-2"></i>
              {{ template.Name }}
            </td>
            <td>
              <span class="type-badge" :class="'type-' + template.Type.toLowerCase()">
                {{ template.Type }}
              </span>
            </td>
            <td>{{ template.CreatedAt | dateformat('MM/DD/YYYY') }}</td>
            <td>{{ template.UsageCount }}</td>
            <td @click.stop>
              <b-dropdown variant="link" no-caret right toggle-class="action-btn">
                <template #button-content>
                  <i class="fa fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item @click="createNewsletter(template)">
                  <i class="fa fa-paper-plane mr-2"></i> Create Newsletter
                </b-dropdown-item>
                <b-dropdown-item @click="renameTemplate(template)">
                  <i class="fa fa-i-cursor mr-2"></i> Rename
                </b-dropdown-item>
                <b-dropdown-item @click="editTemplate(template)">
                  <i class="fa fa-pencil mr-2"></i> Edit
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="duplicateTemplate(template)">
                  <i class="fa fa-copy mr-2"></i> Duplicate
                </b-dropdown-item>
                <b-dropdown-item @click="downloadHtml(template)">
                  <i class="fa fa-download mr-2"></i> Download HTML
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="confirmDeleteTemplate(template)" class="text-danger">
                  <i class="fa fa-trash mr-2"></i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <div class="pagination-info">
        Showing {{ startItem }}-{{ endItem }} of {{ filteredTemplates.length }}
      </div>
      <div class="pagination-controls">
        <button :disabled="currentPage === 1" @click="currentPage--">
          <i class="fa fa-chevron-left"></i>
        </button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button :disabled="currentPage === totalPages" @click="currentPage++">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Create/Edit Template Modal -->
    <b-modal
      ref="templateModal"
      :title="editingTemplate ? 'Edit Template' : 'Create Template'"
      size="lg"
      centered
      @ok="saveTemplate"
    >
      <b-form @submit.prevent="saveTemplate">
        <b-form-group label="Template Name" label-for="template-name">
          <b-form-input
            id="template-name"
            v-model="templateForm.Name"
            placeholder="Enter template name"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Type" label-for="template-type">
          <b-form-select
            id="template-type"
            v-model="templateForm.Type"
            :options="createTypeOptions"
          ></b-form-select>
        </b-form-group>

        <b-form-group label="Subject Line" label-for="template-subject">
          <b-form-input
            id="template-subject"
            v-model="templateForm.Subject"
            placeholder="Enter email subject"
          ></b-form-input>
        </b-form-group>

        <b-form-group v-if="templateForm.Type === 'SMS' || templateForm.Type === 'Both'" label="SMS Content">
          <b-form-textarea
            v-model="templateForm.SmsContent"
            placeholder="Enter SMS message content"
            rows="3"
            max-rows="6"
          ></b-form-textarea>
          <small class="text-muted">{{ (templateForm.SmsContent || '').length }} / 160 characters</small>
        </b-form-group>

        <b-form-group v-if="templateForm.Type === 'Email' || templateForm.Type === 'Both'" label="Email Content">
          <b-form-textarea
            v-model="templateForm.Content"
            placeholder="Enter email HTML content"
            rows="8"
          ></b-form-textarea>
        </b-form-group>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="primary" @click="ok()">
          <i class="fa fa-save mr-2"></i>
          {{ editingTemplate ? 'Update' : 'Create' }} Template
        </b-button>
      </template>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      ref="deleteModal"
      title="Delete Template"
      centered
      @ok="deleteTemplate"
    >
      <p>Are you sure you want to delete the template <strong>"{{ templateToDelete?.Name }}"</strong>?</p>
      <p class="text-danger mb-0">This action cannot be undone.</p>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="danger" @click="ok()">Delete</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions, mapGetters } from 'vuex';
import CommonOverlay from '@/components/CommonOverlay';

export default {
  name: 'CampaignTemplates',

  components: {
    CommonOverlay
  },

  data() {
    return {
      loading: false,
      searchQuery: '',
      filterType: 'all',
      viewMode: 'grid',
      currentPage: 1,
      perPage: 12,
      sortField: 'CreatedAt',
      sortDirection: 'desc',

      // Form state
      editingTemplate: null,
      templateToDelete: null,
      templateForm: {
        Name: '',
        Type: 'Email',
        Subject: '',
        Content: '',
        SmsContent: ''
      },

      // Options
      typeOptions: [
        { value: 'all', text: 'All Types' },
        { value: 'Email', text: 'Email' },
        { value: 'SMS', text: 'SMS' },
        { value: 'Both', text: 'SMS & Email' }
      ],
      createTypeOptions: [
        { value: 'Email', text: 'Email' },
        { value: 'SMS', text: 'SMS' },
        { value: 'Both', text: 'SMS & Email' }
      ],

      // Mock templates
      templates: [
        {
          TemplateID: '1',
          Name: 'Appointment Reminder',
          Type: 'Email',
          Subject: 'Your appointment is coming up!',
          Content: '<h1>Appointment Reminder</h1><p>Dear {{FirstName}},</p><p>This is a reminder of your upcoming appointment.</p>',
          CreatedAt: '2024-01-10T08:00:00Z',
          UpdatedAt: '2024-03-01T14:30:00Z',
          UsageCount: 45
        },
        {
          TemplateID: '2',
          Name: 'Welcome New Patient',
          Type: 'Email',
          Subject: 'Welcome to {{PracticeName}}!',
          Content: '<h1>Welcome!</h1><p>Dear {{FirstName}},</p><p>We are excited to have you as a new patient.</p>',
          CreatedAt: '2024-01-15T09:00:00Z',
          UpdatedAt: '2024-02-20T11:00:00Z',
          UsageCount: 128
        },
        {
          TemplateID: '3',
          Name: 'Recall Notification',
          Type: 'Both',
          Subject: 'Time for your checkup, {{FirstName}}!',
          SmsContent: 'Hi {{FirstName}}! It\'s time for your dental checkup at {{PracticeName}}. Call us to schedule!',
          Content: '<h1>Time for a Checkup</h1><p>Dear {{FirstName}},</p><p>It has been 6 months since your last visit.</p>',
          CreatedAt: '2024-02-01T10:00:00Z',
          UpdatedAt: '2024-03-10T16:00:00Z',
          UsageCount: 89
        },
        {
          TemplateID: '4',
          Name: 'Appointment Confirmation SMS',
          Type: 'SMS',
          SmsContent: 'Your appointment at {{PracticeName}} is confirmed for {{AppointmentDate}} at {{AppointmentTime}}. Reply C to confirm.',
          CreatedAt: '2024-02-15T11:00:00Z',
          UpdatedAt: '2024-02-15T11:00:00Z',
          UsageCount: 234
        },
        {
          TemplateID: '5',
          Name: 'Holiday Greetings',
          Type: 'Email',
          Subject: 'Happy Holidays from {{PracticeName}}!',
          Content: '<h1>Season\'s Greetings!</h1><p>Dear {{FirstName}},</p><p>Wishing you a wonderful holiday season from all of us at {{PracticeName}}.</p>',
          CreatedAt: '2023-12-01T09:00:00Z',
          UpdatedAt: '2023-12-15T14:00:00Z',
          UsageCount: 67
        }
      ]
    };
  },

  computed: {
    sortedTemplates() {
      const templates = [...this.templates];
      return templates.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (this.sortField === 'Name') {
          aVal = (aVal || '').toLowerCase();
          bVal = (bVal || '').toLowerCase();
        }

        if (this.sortField === 'CreatedAt') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    },

    filteredTemplates() {
      let filtered = [...this.sortedTemplates];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(t =>
          t.Name.toLowerCase().includes(query) ||
          t.Subject?.toLowerCase().includes(query)
        );
      }

      // Type filter
      if (this.filterType !== 'all') {
        filtered = filtered.filter(t => t.Type === this.filterType);
      }

      return filtered;
    },

    paginatedTemplates() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredTemplates.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredTemplates.length / this.perPage);
    },

    startItem() {
      return (this.currentPage - 1) * this.perPage + 1;
    },

    endItem() {
      const end = this.currentPage * this.perPage;
      return end > this.filteredTemplates.length ? this.filteredTemplates.length : end;
    }
  },

  mounted() {
    this.loadTemplates();
  },

  methods: {
    async loadTemplates() {
      this.loading = true;
      try {
        // In production, fetch from Vuex store
        // await this.$store.dispatch('campaigns/fetchCampaignTemplates');
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        this.loading = false;
      }
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
      this.currentPage = 1;
    },

    getSortIcon(field) {
      if (this.sortField !== field) return 'fa fa-sort';
      return this.sortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    getTypeIcon(type) {
      const icons = {
        'SMS': 'fa fa-comment text-success',
        'Email': 'fa fa-envelope text-primary',
        'Both': 'fa fa-comments text-info'
      };
      return icons[type] || 'fa fa-file-text';
    },

    openCreateTemplateModal() {
      this.editingTemplate = null;
      this.templateForm = {
        Name: '',
        Type: 'Email',
        Subject: '',
        Content: '',
        SmsContent: ''
      };
      this.$refs.templateModal.show();
    },

    editTemplate(template) {
      this.editingTemplate = template;
      this.templateForm = { ...template };
      this.$refs.templateModal.show();
    },

    async saveTemplate() {
      if (!this.templateForm.Name) {
        this.$toast?.error?.('Please enter a template name');
        return;
      }

      try {
        if (this.editingTemplate) {
          // Update existing
          const index = this.templates.findIndex(t => t.TemplateID === this.editingTemplate.TemplateID);
          if (index > -1) {
            this.templates[index] = {
              ...this.templateForm,
              UpdatedAt: new Date().toISOString()
            };
          }
          this.$toast?.success?.('Template updated successfully');
        } else {
          // Create new
          const newTemplate = {
            ...this.templateForm,
            TemplateID: Date.now().toString(),
            CreatedAt: new Date().toISOString(),
            UpdatedAt: new Date().toISOString(),
            UsageCount: 0
          };
          this.templates.unshift(newTemplate);
          this.$toast?.success?.('Template created successfully');
        }
        this.$refs.templateModal.hide();
      } catch (error) {
        console.error('Error saving template:', error);
        this.$toast?.error?.('Failed to save template');
      }
    },

    renameTemplate(template) {
      const newName = prompt('Enter new template name:', template.Name);
      if (newName && newName.trim()) {
        const index = this.templates.findIndex(t => t.TemplateID === template.TemplateID);
        if (index > -1) {
          this.templates[index].Name = newName.trim();
          this.$toast?.success?.('Template renamed successfully');
        }
      }
    },

    duplicateTemplate(template) {
      const newTemplate = {
        ...template,
        TemplateID: Date.now().toString(),
        Name: `${template.Name} (Copy)`,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        UsageCount: 0
      };
      this.templates.unshift(newTemplate);
      this.$toast?.success?.('Template duplicated successfully');
    },

    downloadHtml(template) {
      const content = template.Content || '';
      const blob = new Blob([content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.Name.replace(/\s+/g, '-')}.html`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      this.$toast?.success?.('HTML downloaded successfully');
    },

    confirmDeleteTemplate(template) {
      this.templateToDelete = template;
      this.$refs.deleteModal.show();
    },

    deleteTemplate() {
      if (!this.templateToDelete) return;

      const index = this.templates.findIndex(t => t.TemplateID === this.templateToDelete.TemplateID);
      if (index > -1) {
        this.templates.splice(index, 1);
        this.$toast?.success?.('Template deleted successfully');
      }
      this.$refs.deleteModal.hide();
      this.templateToDelete = null;
    },

    createNewsletter(template) {
      // Navigate to campaign creation with template pre-selected
      this.$router.push({
        name: 'campaigns',
        query: { templateId: template.TemplateID }
      });
    }
  },

  filters: {
    relativeDate(dateStr) {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    }
  }
};
</script>

<style lang="scss" scoped>
.templates-page {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.templates-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  .page-subtitle {
    color: #6c757d;
    margin: 4px 0 0;
    font-size: 14px;
  }
}

// Sub-navigation for Campaigns section
.campaigns-subnav {
  display: flex;
  gap: 4px;
  background: white;
  padding: 6px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow-x: auto;

  .subnav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
    transition: all 0.2s ease;

    i {
      font-size: 14px;
    }

    &:hover {
      background: #f1f5f9;
      color: #334155;
    }

    &.active,
    &.router-link-exact-active {
      background: linear-gradient(135deg, #3abf94, #2d9474);
      color: white;
      box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);
    }
  }

  @media (max-width: 576px) {
    .subnav-link {
      padding: 8px 12px;
      font-size: 0.85rem;

      span {
        display: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.templates-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .search-box {
    position: relative;
    flex: 1;
    min-width: 250px;
    max-width: 400px;

    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
    }

    .search-input {
      width: 100%;
      padding: 10px 36px 10px 40px;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #007bff;
      }
    }

    .clear-btn {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
      cursor: pointer;

      &:hover {
        color: #6c757d;
      }
    }
  }

  .filter-select {
    width: 150px;
    border-radius: 8px;
  }

  .view-toggle {
    display: flex;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;

    .view-btn {
      padding: 8px 12px;
      border: none;
      background: transparent;
      color: #6c757d;
      cursor: pointer;

      &:hover {
        background: #f8f9fa;
      }

      &.active {
        background: #007bff;
        color: #fff;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;

  .empty-icon {
    font-size: 64px;
    color: #dee2e6;
    margin-bottom: 16px;
  }

  h4 {
    color: #212529;
    margin-bottom: 8px;
  }

  p {
    color: #6c757d;
    margin-bottom: 16px;
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  .template-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    position: relative;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .card-actions {
        opacity: 1;
      }
    }

    .card-preview {
      height: 120px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;

      .preview-placeholder {
        width: 60px;
        height: 60px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 28px;
          color: #fff;
        }
      }
    }

    .card-content {
      padding: 16px;

      .template-name {
        font-size: 16px;
        font-weight: 600;
        color: #212529;
        margin: 0 0 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .template-meta {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;

        .usage-count {
          font-size: 12px;
          color: #6c757d;
        }
      }

      .template-date {
        font-size: 12px;
        color: #adb5bd;
      }
    }

    .card-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      opacity: 0;
      transition: opacity 0.2s;

      .action-btn {
        background: rgba(255, 255, 255, 0.9);
        border-radius: 4px;
        padding: 4px 8px;
        color: #6c757d;

        &:hover {
          background: #fff;
          color: #212529;
        }
      }
    }
  }
}

.templates-list {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  .templates-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    th {
      font-size: 12px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      background: #f8f9fa;

      &.sortable {
        cursor: pointer;

        &:hover {
          background: #e9ecef;
        }
      }
    }

    td {
      font-size: 14px;
    }

    .name-cell {
      font-weight: 500;
      cursor: pointer;

      &:hover {
        color: #007bff;
      }
    }

    .action-btn {
      padding: 4px 8px;
      color: #6c757d;

      &:hover {
        color: #212529;
      }
    }
  }
}

.type-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;

  &.type-email {
    background: #cce5ff;
    color: #004085;
  }

  &.type-sms {
    background: #d4edda;
    color: #155724;
  }

  &.type-both {
    background: #d1ecf1;
    color: #0c5460;
  }
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .pagination-info {
    font-size: 13px;
    color: #6c757d;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 12px;

    button {
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      padding: 6px 10px;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #f8f9fa;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    span {
      font-size: 13px;
      color: #6c757d;
    }
  }
}
</style>
